import { useEffect, useRef, useState } from "react";
import type { PropsWithChildren } from "react";

import { isEmpty } from "lodash";

import { useGetUsStatesQuery } from "@/store/api/userEnums";
import MultiSelectTextField from "@/ui/composed/pickers/MultiSelectTextField";
import TextField from "@/ui/core/TextField";
import { Box, HStack, VStack } from "@/ui/layout";
import { usStatesEnumToMultiSelectOptions } from "@/utils/api/enums.util";
import { joinTestIDs } from "@/utils/string.util";

type AddressKey = keyof Pick<
  GooglePlacesAddress,
  "addressLine1" | "addressLine2" | "city" | "state" | "zip"
>;

const FieldName: Record<string, AddressKey> = {
  AddressLine1: "addressLine1",
  AddressLine2: "addressLine2",
  City: "city",
  State: "state",
  Zip: "zip",
};

const isAddressFieldEmpty = (value: null | string | undefined): boolean => {
  return isEmpty(value?.trim());
};

interface AddressFieldProps extends ViewWithTestID, PropsWithChildren {
  hasError?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  onChange: (value: GooglePlacesAddress) => void;
  value?: GooglePlacesAddress;
}

const AddressField = ({
  hasError,
  isDisabled,
  isRequired,
  onChange,
  testID,
  value,
}: AddressFieldProps) => {
  const { data: usStatesData } = useGetUsStatesQuery();
  const stateOptions = usStatesEnumToMultiSelectOptions(usStatesData);
  const [address, setAddress] = useState<GooglePlacesAddress>(
    value ?? {
      addressLine1: undefined,
      addressLine2: undefined,
      city: undefined,
      state: undefined,
      zip: undefined,
    },
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({
    [FieldName.AddressLine1]: false,
    [FieldName.AddressLine2]: false,
    [FieldName.City]: false,
    [FieldName.State]: false,
    [FieldName.Zip]: false,
  });

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current(address);
  }, [address]);

  useEffect(() => {
    if (!isRequired) {
      setFieldErrors({
        [FieldName.AddressLine1]: false,
        [FieldName.AddressLine2]: false,
        [FieldName.City]: false,
        [FieldName.State]: false,
        [FieldName.Zip]: false,
      });
      return;
    }
    if (hasError) {
      setFieldErrors({
        [FieldName.AddressLine1]: isAddressFieldEmpty(address.addressLine1),
        [FieldName.AddressLine2]: false,
        [FieldName.City]: isAddressFieldEmpty(address.city),
        [FieldName.State]: isAddressFieldEmpty(address.state),
        [FieldName.Zip]: isAddressFieldEmpty(address.zip),
      });
    }
  }, [hasError, isRequired, address]);

  const handleFieldChange =
    (fieldName: keyof GooglePlacesAddress) =>
    (newValue: null | string | undefined) => {
      setAddress((prev) => ({ ...prev, [fieldName]: newValue }));
      // clear error only when user fills in the field
      if (!isAddressFieldEmpty(newValue)) {
        setFieldErrors((prev) => ({ ...prev, [fieldName]: false }));
      }
    };

  return (
    <VStack gap={12}>
      <TextField
        hasError={fieldErrors.addressLine1}
        isDisabled={isDisabled}
        onChange={handleFieldChange("addressLine1")}
        placeholder="Street address"
        testID={joinTestIDs(testID, "addressLine1")}
        value={address.addressLine1}
      />
      <TextField
        hasError={false}
        isDisabled={isDisabled}
        onChange={handleFieldChange("addressLine2")}
        placeholder="Apt, suite, unit (optional)"
        testID={joinTestIDs(testID, "addressLine2")}
        value={address.addressLine2}
      />
      <TextField
        hasError={fieldErrors.city}
        isDisabled={isDisabled}
        onChange={handleFieldChange("city")}
        placeholder="City"
        testID={joinTestIDs(testID, "city")}
        value={address.city}
      />
      <HStack gap={8}>
        <Box flex={1}>
          <MultiSelectTextField
            hasError={fieldErrors.state}
            isDisabled={isDisabled}
            isSingleSelect
            modalTitle="State"
            onValuesChange={(selectedOptions) => {
              const selectedState = selectedOptions[0]?.value ?? "";
              handleFieldChange("state")(selectedState);
            }}
            options={stateOptions}
            testID={joinTestIDs(testID, "state")}
            value={address.state}
          />
        </Box>
        <Box flex={1}>
          <TextField
            hasError={fieldErrors.zip}
            isDisabled={isDisabled}
            onChange={handleFieldChange("zip")}
            placeholder="ZIP code"
            testID={joinTestIDs(testID, "zip")}
            value={address.zip}
          />
        </Box>
      </HStack>
    </VStack>
  );
};

export type { AddressFieldProps };
export default AddressField;
